import React, { useRef } from 'react';
import { Box, VStack, Button } from '@chakra-ui/react';
import { useTenant } from '../../contexts/TenantContext';
import { formatInTenantTz } from '../../utils/formatDate';
import ChatBubble from './ChatBubble';
import DateDivider from './DateDivider';

const MESSAGE_PAGE_SIZE = 10;

/**
 * ChatMessageList Component
 * When onPageChange is provided: fixed window (no scroll), use Load older / Back to latest.
 * When onPageChange is not provided: shows all messages, container should have overflow hidden and fixed height.
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {number} props.messagePage - Current page (0 = most recent page), used only if onPageChange provided
 * @param {Function} props.onPageChange - (page: number) => void; if omitted, all messages shown
 * @param {Function} props.onEditMessage - Callback when clicking editable message
 */
const ChatMessageList = ({
  messages = [],
  messagePage = 0,
  onPageChange,
  onEditMessage,
}) => {
  const containerRef = useRef(null);
  const { tenantTimezone } = useTenant();

  const total = messages.length;
  const usePagination = typeof onPageChange === 'function';
  const start = usePagination ? Math.max(0, total - (messagePage + 1) * MESSAGE_PAGE_SIZE) : 0;
  const end = usePagination ? total - messagePage * MESSAGE_PAGE_SIZE : total;
  const visibleMessages = messages.slice(start, end);
  const hasOlder = usePagination && start > 0;
  const hasNewer = usePagination && messagePage > 0;

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    msgs.forEach((msg) => {
      const msgDate = formatInTenantTz(msg.timestamp || Date.now(), tenantTimezone, {
        dateStyle: 'long',
        timeStyle: undefined,
      });

      if (msgDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup });
        }
        currentDate = msgDate;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup });
    }

    return groups;
  };

  const messageGroups = groupMessagesByDate(visibleMessages);

  // Format timestamp for display
  const formatTime = (timestamp) => {
    if (timestamp == null || timestamp === '') return '';
    return formatInTenantTz(timestamp, tenantTimezone, {
      dateStyle: undefined,
      timeStyle: undefined,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Box ref={containerRef} flex="1" minH={0} overflow="hidden" display="flex" flexDirection="column" px={4} py={4} bg="white">
      {hasOlder && onPageChange && (
        <Box pb={2} flexShrink={0}>
          <Button size="sm" variant="link" colorScheme="blue" onClick={() => onPageChange(messagePage + 1)}>
            Load older messages
          </Button>
        </Box>
      )}
      <VStack spacing={0} align="stretch" flex="1" minH={0} overflow="hidden">
        {messageGroups.map((group, groupIndex) => (
          <Box
            key={groupIndex}
            sx={{ contentVisibility: 'auto', containIntrinsicSize: '0 80px' }}
          >
            {/* Date Divider */}
            <DateDivider date={group.date} />

            {/* Messages in this date group */}
            {group.messages.map((message, msgIndex) => (
              <ChatBubble
                key={`${groupIndex}-${msgIndex}`}
                role={message.role}
                content={message.content}
                timestamp={formatTime(message.timestamp)}
                intent={message.intent}
                metadata={message.metadata}
                isEditable={message.isEditable || false}
                onEdit={onEditMessage}
              />
            ))}
          </Box>
        ))}

        {hasNewer && onPageChange && (
          <Box pt={2} flexShrink={0}>
            <Button size="sm" variant="link" colorScheme="blue" onClick={() => onPageChange(messagePage - 1)}>
              Back to latest
            </Button>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default ChatMessageList;
